import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";

import { Link, useLocation } from "react-router-dom";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { dateFormatter } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";

import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";

import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";
import TextBadge from "../../common/info-badge/TextBadge";
import {
  changeToToman,
  formatCurrency,
} from "../../../utils/currency-formater";
import { routerPaths } from "../../../constants/Paths";
import { MoneySend, TrendDown, TrendUp } from "iconsax-react";

import {
  WalletListRequest,
} from "../../../services/Endpoints/wallet/WalletListEndpoint";
import { useUrl } from "../../../hooks/urlEncoding";
import {
  WalletTransactionListColumnFilterable,
  WalletTransactionListColumnSortable,
} from "../../../services/Endpoints/wallet/WalletTransactionListEndpoint";
import LinkWithPopover from "../common/LinkWithPopover";
import ChargeModal from "../charge-modal/ChargeModal";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import UserLink from "../common/UserLink";
import { WorkspaceListColumnFilterable } from "../../../services/Endpoints/workspaces";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import useUrlSort from "../../../hooks/useUrlSort";

interface ITransaction {
  workspace_id: number | ReactNode;
  balance: number | ReactNode;
  locked_balance: number | ReactNode;
  created_at: string;
  operations: string | ReactNode;
}

export interface ISupportTabsComponent {
  walletReload: boolean;
  setWalletReload: (payload: boolean) => void;
}

export default function WalletTableComponent({
  walletReload,
  setWalletReload,
}: Readonly<ISupportTabsComponent>) {
  const {
    queryStringUrl,
    parseQueryString,
    addToQueryStringUrl,
    setParamWithSearchParams,
  } = useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const [value] = useState("1");
  const { lang } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [dataTransaction, setDataTransaction] = useState<ITransaction[] | []>(
    []
  );
  const [reload, setReload] = useState(false);
  const [total, setTotal] = useState(0);
  const [openIncreaseModal, setOpenIncreaseModal] = useState(0);
  const [openDecreaseModal, setOpenDecreaseModal] = useState(0);
  const [waiting, setWaiting] = useState(false);
  const { can } = useCanAccess();
  const location = useLocation();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();

  const getDataTransaction = async () => {
    try {
      setWaiting(true);
      const walletListRequest = new WalletListRequest(protectedInstance);
      const resp = await walletListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const transactions = resp.data.wallets.map(
        ({ created_at, balance, locked_balance, workspace_id, id }) => ({
          created_at,
          id,
          operations: (
            <Stack justifyContent="center" alignItems="center" direction="row">
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminTransactionsList)}
                title="تراکنش های کیف پول"
              >
                <Stack
                  direction="row"
                  justifyContent="center"
                  gap="4px"
                  component={Link}
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",
                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                    },
                  }}
                  to={queryStringUrl(routerPaths.WALLET_TRANSACTION, {
                    filters: {
                      [WalletTransactionListColumnFilterable.WALLET_ID]: {
                        operator: FilterOperatorEnum.EQUAL,
                        column: WalletTransactionListColumnFilterable.WALLET_ID,
                        value: workspace_id,
                      },
                    },
                    sorts: {
                      [WalletTransactionListColumnSortable.ID]: {
                        column: WalletTransactionListColumnSortable.ID,
                        direction: SortDirectionEnum.DESC,
                      },
                    },
                  })}
                >
                  <MoneySend size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminWalletsIncrease)}
                title="افرایش اعتبار"
              >
                <Stack
                  direction="row"
                  justifyContent="center"
                  gap="4px"
                  onClick={() => setOpenIncreaseModal(id)}
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",

                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                    },
                  }}
                >
                  <TrendUp size="20" />
                </Stack>
              </LinkWithPopover>
              <LinkWithPopover
                disabled={!can(AccessEnums.AdminWalletsDecrease)}
                title="کاهش اعتبار"
              >
                <Stack
                  direction="row"
                  justifyContent="center"
                  onClick={() => setOpenDecreaseModal(id)}
                  gap="4px"
                  sx={{
                    padding: "4px",
                    borderRadius: "50%",

                    cursor: "pointer",
                    color: (theme) => theme.palette.complement[400],
                    "&:hover": {
                      color: (theme) => theme.palette.others.link,
                    },
                  }}
                >
                  <TrendDown size="20" />
                </Stack>
              </LinkWithPopover>
            </Stack>
          ),
          balance: (
            <TextBadge
              sxStyle={{
                backgroundColor: ({ palette }: any) => palette.others.doneBg,
                color: ({ palette }: any) => palette.others.done,
              }}
              text={
                <Stack
                  direction="row"
                  gap="4px"
                  justifyContent="center"
                  alignItems="center"
                >
                  {formatCurrency(changeToToman(`${balance}`))}
                </Stack>
              }
            />
          ),
          locked_balance: (
            <TextBadge
              sxStyle={{
                backgroundColor: ({ palette }: any) =>
                  palette.others.deadlineBg,
                color: ({ palette }: any) => palette.others.deadline,
              }}
              text={
                <Stack
                  direction="row"
                  gap="4px"
                  justifyContent="center"
                  alignItems="center"
                >
                  {formatCurrency(changeToToman(`${locked_balance}`))}
                </Stack>
              }
            />
          ),
          workspace_id: (
            <UserLink
              disabled={!can(AccessEnums.AdminWorkspacesList)}
              user={{ id: workspace_id }}
              link={addToQueryStringUrl(routerPaths.WORKSPACES, {
                filters: {
                  [WorkspaceListColumnFilterable.ID]: {
                    column: WorkspaceListColumnFilterable.ID,
                    value: workspace_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
              })}
            />
          ),
        })
      );
      setDataTransaction(transactions);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getDataTransaction();
  }, [walletReload, page, rowsPerPage, value, reload, location.search]);

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه ",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "created_at",
      label: "تاریخ",
      minWidth: 100,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },

    {
      key: "workspace_id",
      label: "شناسه تخته",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "balance",
      label: "مانده",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "locked_balance",
      label: "مانده بلوکه‌شده ",
      minWidth: 170,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "operations",
      label: "‌عملیات ",
      minWidth: 170,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <Box sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}>
      <CustomTable
        reloadAction={() => setWalletReload(!walletReload)}
        hasWaiting={waiting}
        hasSearchFilter
        hasBottomPagination
        hasTopPagination
        columns={columns}
        count={total}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        page={page}
        rows={dataTransaction}
        rowsPerPage={rowsPerPage}
        filtersSchema={filtersSchema}
        onApplySorts={applyUrlSorts}
        sorts={sorts}
      />

      <ChargeModal
        onClose={() => setOpenIncreaseModal(0)}
        walletId={openIncreaseModal}
        increase
        handleReload={() => setReload(!reload)}
      />
      <ChargeModal
        onClose={() => setOpenDecreaseModal(0)}
        walletId={openDecreaseModal}
        handleReload={() => setReload(!reload)}
      />
    </Box>
  );
}
